import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { LoanOfficer, HealthIndicator, computeReviewStatus, getWeekReview } from '../../data/mockData';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ProgressRing } from '../ui/ProgressRing';
import { ChevronDown, ChevronRight, Users, ShieldAlert, X, Map } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LODetails } from '../manager/LODetails';
interface ManagerHierarchyProps {
  rvpId?: string;
  healthFilter?: HealthIndicator | 'all';
}
export function ManagerHierarchy({
  rvpId,
  healthFilter = 'all'
}: ManagerHierarchyProps) {
  const {
    rvps,
    managers,
    loanOfficers,
    selectedWeek
  } = useData();
  const filterActive = healthFilter !== 'all';
  const matchesFilter = (lo: LoanOfficer) => healthFilter === 'all' || lo.healthIndicator === healthFilter;
  const [expandedRvps, setExpandedRvps] = useState<Set<string>>(new Set());
  const [expandedManagers, setExpandedManagers] = useState<Set<string>>(new Set());
  const [viewingLoId, setViewingLoId] = useState<string | null>(null);
  const setViewingLo = (lo: LoanOfficer | null) => setViewingLoId(lo ? lo.id : null);
  // Resolve the live LO from context so review edits (Complete & Next, toggles,
  // scores, notes) reflect immediately in the modal instead of a stale snapshot.
  const viewingLo = loanOfficers.find(l => l.id === viewingLoId) || null;
  const toggleRvp = (id: string) => {
    const newSet = new Set(expandedRvps);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setExpandedRvps(newSet);
  };
  const toggleManager = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSet = new Set(expandedManagers);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setExpandedManagers(newSet);
  };
  const renderManagerTable = (managerId: string) => {
    const los = loanOfficers.filter(lo => lo.managerId === managerId).filter(matchesFilter);
    return <div className="bg-white pl-12 pr-0 relative" data-id="element-39">
        {/* Tree spine: connects manager to its loan officers */}
        <div className="absolute left-7 top-0 bottom-0 w-px bg-slate-200" aria-hidden="true" data-id="element-40" />
        <table className="w-full text-sm text-left border-l border-slate-200" data-id="element-41">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase border-b border-slate-100" data-id="element-42">
            <tr data-id="element-43">
              <th className="px-6 py-3 font-medium" data-id="element-44">Loan Officer</th>
              <th className="px-6 py-3 font-medium" data-id="element-45">Review Status</th>
              <th className="px-6 py-3 font-medium" data-id="element-46">Score</th>
              <th className="px-6 py-3 font-medium" data-id="element-47">Health</th>
              <th className="px-6 py-3 font-medium text-right" data-id="element-48">Action</th>
            </tr>
          </thead>
          <tbody data-id="element-49">
            {los.map(lo => {
            const status = computeReviewStatus(getWeekReview(lo, selectedWeek));
            return <tr key={lo.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-id="element-50">
                  <td className="px-6 py-4 font-medium text-slate-900" data-id="element-51">
                    <div className="flex items-center space-x-3 relative" data-id="element-52">
                      {/* Branch connector off the tree spine */}
                      <span className="absolute -left-5 top-1/2 w-4 h-px bg-slate-200" aria-hidden="true" data-id="element-53" />
                      <img src={lo.avatar} alt={lo.name} className="w-8 h-8 rounded-full" data-id="element-54" />
                      <span data-id="element-55">{lo.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-56">
                    {status === 'complete' ? <Badge variant="success" data-id="element-57">Complete</Badge> : status === 'in_progress' ? <Badge variant="warning" data-id="element-58">In Progress</Badge> : <Badge variant="neutral" data-id="element-59">Not Started</Badge>}
                  </td>
                  <td className="px-6 py-4 font-bold text-slate-900" data-id="element-60">
                    {lo.accountabilityScore}%
                  </td>
                  <td className="px-6 py-4" data-id="element-61">
                    <Badge variant={lo.healthIndicator === 'Healthy' ? 'success' : lo.healthIndicator === 'At Risk' ? 'warning' : 'danger'} data-id="element-62">
                      {lo.healthIndicator}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-63">
                    <button onClick={() => setViewingLo(lo)} className="text-indigo-600 hover:text-indigo-900 font-medium" data-id="element-64">
                      View Review
                    </button>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>;
  };
  const renderManagerRow = (manager: any) => {
    const los = loanOfficers.filter(lo => lo.managerId === manager.id).filter(matchesFilter);
    if (filterActive && los.length === 0) return null;
    const isExpanded = filterActive || expandedManagers.has(manager.id);
    const avgScore = Math.round(los.reduce((acc, lo) => acc + lo.accountabilityScore, 0) / (los.length || 1));
    const atRiskCount = los.filter(lo => lo.healthIndicator !== 'Healthy').length;
    return <div key={manager.id} className="border-b border-slate-100 last:border-0" data-id="element-65">
        <div className="bg-white p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors" onClick={e => toggleManager(manager.id, e)} data-id="element-66">
          <div className="flex items-center space-x-4 pl-4" data-id="element-67">
            {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-68" /> : <ChevronRight className="w-4 h-4 text-slate-400" data-id="element-69" />}
            <img src={manager.avatar} alt={manager.name} className="w-8 h-8 rounded-full border border-slate-200" data-id="element-70" />
            <div data-id="element-71">
              <h4 className="font-semibold text-slate-900 text-sm" data-id="element-72">
                {manager.name}
              </h4>
              <p className="text-xs text-slate-500" data-id="element-73">
                {manager.team} • {los.length} LOs
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-6" data-id="element-74">
            {atRiskCount > 0 && <div className="flex items-center text-amber-600 text-xs font-medium" data-id="element-75">
                <ShieldAlert className="w-3 h-3 mr-1" data-id="element-76" />
                {atRiskCount} At Risk
              </div>}
            <div className="flex items-center space-x-2" data-id="element-77">
              <span className="text-xs font-medium text-slate-500" data-id="element-78">Avg:</span>
              <span className="text-sm font-bold text-slate-900" data-id="element-79">
                {avgScore}%
              </span>
            </div>
          </div>
        </div>
        <AnimatePresence data-id="element-80">
          {isExpanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="overflow-hidden bg-slate-50 border-t border-slate-100" data-id="element-81">
              {renderManagerTable(manager.id)}
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  // If rvpId is provided, just render that RVP's managers (2-tier)
  if (rvpId) {
    const rvpManagers = managers.filter(m => m.rvpId === rvpId);
    return <div className="space-y-4" data-id="element-82">
        <Card className="overflow-hidden border-slate-200" data-id="element-83">
          {rvpManagers.map(renderManagerRow)}
        </Card>
        {/* Modal logic at the bottom */}
        <AnimatePresence data-id="element-84">
          {viewingLo && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 p-4 overflow-y-auto" onClick={() => setViewingLo(null)} role="dialog" aria-modal="true" aria-label={`${viewingLo.name} accountability review`} data-id="element-85">
              <motion.div initial={{
            opacity: 0,
            scale: 0.97,
            y: 12
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.97,
            y: 12
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} className="w-full max-w-5xl my-8 bg-white rounded-xl shadow-xl" onClick={e => e.stopPropagation()} data-id="element-86">
                <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-xl" data-id="element-87">
                  <div className="flex items-center space-x-3" data-id="element-88">
                    <img src={viewingLo.avatar} alt={viewingLo.name} className="w-10 h-10 rounded-full border-2 border-slate-100" data-id="element-89" />
                    <div data-id="element-90">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-91">
                        {viewingLo.name}
                      </h3>
                      <p className="text-sm text-slate-500" data-id="element-92">{viewingLo.team}</p>
                    </div>
                  </div>
                  <button onClick={() => setViewingLo(null)} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors" aria-label="Close review" data-id="element-93">
                    <X className="w-5 h-5" data-id="element-94" />
                  </button>
                </div>
                <div className="px-5 pb-6" data-id="element-95">
                  <LODetails lo={viewingLo} onComplete={() => setViewingLo(null)} data-id="element-96" />
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>;
  }
  // Otherwise, render 3-tier hierarchy for Admins
  return <div className="space-y-4" data-id="element-97">
      {rvps.map(rvp => {
      const rvpManagers = managers.filter(m => m.rvpId === rvp.id);
      const rvpLos = loanOfficers.filter(lo => rvpManagers.some(m => m.id === lo.managerId)).filter(matchesFilter);
      if (filterActive && rvpLos.length === 0) return null;
      const isExpanded = filterActive || expandedRvps.has(rvp.id);
      const avgScore = Math.round(rvpLos.reduce((acc, lo) => acc + lo.accountabilityScore, 0) / (rvpLos.length || 1));
      const atRiskCount = rvpLos.filter(lo => lo.healthIndicator !== 'Healthy').length;
      return <Card key={rvp.id} className="overflow-hidden border-slate-200" data-id="element-98">
            <div className="bg-slate-50 p-5 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => toggleRvp(rvp.id)} data-id="element-99">
              <div className="flex items-center space-x-4" data-id="element-100">
                {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-500" data-id="element-101" /> : <ChevronRight className="w-5 h-5 text-slate-500" data-id="element-102" />}
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center border border-indigo-200" data-id="element-103">
                  <Map className="w-5 h-5 text-indigo-600" data-id="element-104" />
                </div>
                <div data-id="element-105">
                  <div className="flex items-center" data-id="element-106">
                    <h3 className="font-bold text-slate-900" data-id="element-107">{rvp.name}</h3>
                    {(rvp as any).isJoint && <Badge variant="neutral" className="ml-2" data-id="element-108">
                        Joint
                      </Badge>}
                  </div>
                  <p className="text-xs text-slate-500" data-id="element-109">
                    {rvp.region} • {rvpManagers.length} Managers
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-8" data-id="element-110">
                {atRiskCount > 0 && <div className="flex items-center text-amber-600 text-sm font-medium" data-id="element-111">
                    <ShieldAlert className="w-4 h-4 mr-1" data-id="element-112" />
                    {atRiskCount} At Risk
                  </div>}
                <div className="flex items-center space-x-2" data-id="element-113">
                  <span className="text-sm font-medium text-slate-600" data-id="element-114">
                    Region Avg:
                  </span>
                  <ProgressRing value={avgScore} size={36} strokeWidth={4} showValue={false} data-id="element-115" />
                  <span className="text-sm font-bold text-slate-900" data-id="element-116">
                    {avgScore}%
                  </span>
                </div>
              </div>
            </div>

            <AnimatePresence data-id="element-117">
              {isExpanded && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-slate-200" data-id="element-118">
                  {rvpManagers.length > 0 ? rvpManagers.map(renderManagerRow) : <div className="p-6 text-center text-sm text-slate-500 bg-white" data-id="element-119">
                      No managers assigned yet.
                    </div>}
                </motion.div>}
            </AnimatePresence>
          </Card>;
    })}

      {/* Unassigned Managers Pseudo-Group */}
      {(() => {
      const unassignedManagers = managers.filter(m => !m.rvpId);
      if (unassignedManagers.length === 0) return null;
      const isExpanded = filterActive || expandedRvps.has('unassigned');
      // Calculate stats for unassigned managers' LOs
      const unassignedLos = loanOfficers.filter(lo => unassignedManagers.some(m => m.id === lo.managerId)).filter(matchesFilter);
      if (filterActive && unassignedLos.length === 0) return null;
      const avgScore = Math.round(unassignedLos.reduce((acc, lo) => acc + lo.accountabilityScore, 0) / (unassignedLos.length || 1));
      const atRiskCount = unassignedLos.filter(lo => lo.healthIndicator !== 'Healthy').length;
      return <Card key="unassigned" className="overflow-hidden border-slate-200" data-id="element-120">
            <div className="bg-slate-50 p-5 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => toggleRvp('unassigned')} data-id="element-121">
              <div className="flex items-center space-x-4" data-id="element-122">
                {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-500" data-id="element-123" /> : <ChevronRight className="w-5 h-5 text-slate-500" data-id="element-124" />}
                <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center border border-slate-300" data-id="element-125">
                  <Users className="w-5 h-5 text-slate-600" data-id="element-126" />
                </div>
                <div data-id="element-127">
                  <h3 className="font-bold text-slate-900" data-id="element-128">
                    Unassigned Managers
                  </h3>
                  <p className="text-xs text-slate-500" data-id="element-129">
                    Not reporting to an RVP • {unassignedManagers.length}{' '}
                    Managers
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-8" data-id="element-130">
                {atRiskCount > 0 && <div className="flex items-center text-amber-600 text-sm font-medium" data-id="element-131">
                    <ShieldAlert className="w-4 h-4 mr-1" data-id="element-132" />
                    {atRiskCount} At Risk
                  </div>}
                <div className="flex items-center space-x-2" data-id="element-133">
                  <span className="text-sm font-medium text-slate-600" data-id="element-134">
                    Avg:
                  </span>
                  <ProgressRing value={avgScore} size={36} strokeWidth={4} showValue={false} data-id="element-135" />
                  <span className="text-sm font-bold text-slate-900" data-id="element-136">
                    {avgScore}%
                  </span>
                </div>
              </div>
            </div>

            <AnimatePresence data-id="element-137">
              {isExpanded && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-slate-200" data-id="element-138">
                  {unassignedManagers.map(renderManagerRow)}
                </motion.div>}
            </AnimatePresence>
          </Card>;
    })()}

      <AnimatePresence data-id="element-139">
        {viewingLo && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 p-4 overflow-y-auto" onClick={() => setViewingLo(null)} role="dialog" aria-modal="true" aria-label={`${viewingLo.name} accountability review`} data-id="element-140">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97,
          y: 12
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.97,
          y: 12
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} className="w-full max-w-5xl my-8 bg-white rounded-xl shadow-xl" onClick={e => e.stopPropagation()} data-id="element-141">
              <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-xl" data-id="element-142">
                <div className="flex items-center space-x-3" data-id="element-143">
                  <img src={viewingLo.avatar} alt={viewingLo.name} className="w-10 h-10 rounded-full border-2 border-slate-100" data-id="element-144" />
                  <div data-id="element-145">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-146">
                      {viewingLo.name}
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-147">{viewingLo.team}</p>
                  </div>
                </div>
                <button onClick={() => setViewingLo(null)} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors" aria-label="Close review" data-id="element-148">
                  <X className="w-5 h-5" data-id="element-149" />
                </button>
              </div>
              <div className="px-5 pb-6" data-id="element-150">
                <LODetails lo={viewingLo} onComplete={() => setViewingLo(null)} data-id="element-151" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}